'use client';

/**
 * Cart Page
 * Shopping cart view with item management and checkout navigation
 *
 * Note: HubSpot deal payment links are handled at /cart/hubspot
 */
import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '@/contexts/CartContext';
import { useAvailability } from '@/hooks/useAvailability';
import { BackorderNotice } from '@/components/product/BackorderNotice';

// Types matching cart.service.ts
interface CartProductImage {
  id: number;
  url: string;
  altText: string | null;
  position: number;
  role: string | null;
}
interface CartProductSummary {
  id: number;
  sku: string;
  name: string;
  slug: string;
  basePrice: string;
  status: string;
  images: CartProductImage[];
}
interface SelectedOption {
  optionId: number;
  valueId: number;
  title?: string;
  valueTitle?: string;
}
interface CartItemWithProduct {
  id: number;
  cartId: number;
  productId: number;
  quantity: number;
  selectedOptions: SelectedOption[];
  product: CartProductSummary;
  calculatedPrice: string;
  lineTotal: string;
}
interface CartWithItems {
  id: number;
  sessionId: string | null;
  customerId: number | null;
  items: CartItemWithProduct[];
  itemCount: number;
  subtotal: string;
}
function formatPrice(price: number | string): string {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(numPrice);
}
export function CartPageClient() {
  const router = useRouter();
  const {
    refreshCart
  } = useCart();
  const [cart, setCart] = useState<CartWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingItems, setUpdatingItems] = useState<Set<number>>(new Set());

  // Live backorder/availability lookup per cart line. Pass the real quantity so a
  // line requesting more than is available shows the same verdict checkout enforces.
  const availability = useAvailability(cart?.items.map(item => ({
    sku: item.product.sku,
    quantity: item.quantity
  })) ?? []);
  const fetchCart = useCallback(async () => {
    try {
      const sessionId = localStorage.getItem('cart_session_id');
      if (!sessionId) {
        setCart({
          id: 0,
          sessionId: null,
          customerId: null,
          items: [],
          itemCount: 0,
          subtotal: '0.00'
        });
        return;
      }
      const response = await fetch(`/api/cart?sessionId=${sessionId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      const {
        data
      } = await response.json();
      setCart(data ?? {
        id: 0,
        sessionId: null,
        customerId: null,
        items: [],
        itemCount: 0,
        subtotal: '0.00'
      });
    } catch {
      setError('Failed to load cart. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);
  const updateQuantity = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      const sessionId_0 = localStorage.getItem('cart_session_id');
      const query = sessionId_0 ? `?sessionId=${encodeURIComponent(sessionId_0)}` : '';
      const response_0 = await fetch(`/api/cart/items/${itemId}${query}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity: newQuantity
        })
      });
      if (!response_0.ok) {
        throw new Error('Failed to update quantity');
      }
      const data_0 = await response_0.json();
      setCart(data_0);
      refreshCart();
    } catch {
      toast.error('Failed to update quantity');
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };
  const removeItem = async (itemId_0: number) => {
    setUpdatingItems(prev_0 => new Set(prev_0).add(itemId_0));
    try {
      const sessionId_1 = localStorage.getItem('cart_session_id');
      const query_0 = sessionId_1 ? `?sessionId=${encodeURIComponent(sessionId_1)}` : '';
      const response_1 = await fetch(`/api/cart/items/${itemId_0}${query_0}`, {
        method: 'DELETE'
      });
      if (!response_1.ok) {
        throw new Error('Failed to remove item');
      }
      const data_1 = await response_1.json();
      setCart(data_1);
      refreshCart();
      toast.success('Item removed from cart');
    } catch {
      toast.error('Failed to remove item');
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId_0);
        return next;
      });
    }
  };
  if (loading) {
    return <div className="page-container flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>;
  }
  if (error) {
    return <div className="page-container py-8 sm:py-12">
        <Alert variant="destructive">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      </div>;
  }
  if (!cart || cart.items.length === 0) {
    return <div className="page-container py-8 sm:py-12">
        <div className="max-w-md mx-auto text-center">
          <ShoppingBag className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground/50 mb-3 sm:mb-4" />
          <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Your cart is empty</h1>
          <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
            Looks like you haven&apos;t added anything to your cart yet.
          </p>
          <Button onClick={() => router.push('/')} className="h-12 px-6 text-base">
            Continue Shopping
          </Button>
        </div>
      </div>;
  }
  const subtotal = parseFloat(cart.subtotal || '0');
  return <div className="page-container">
      <h1 className="text-page-title font-bold mb-4 sm:mb-6 lg:mb-8">Shopping Cart</h1>

      {/* Cart Layout - stack on mobile, sidebar on lg+ */}
      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 cart-items-scroll space-y-2 sm:space-y-3">
          {cart.items.map(item_0 => {
          const isUpdating = updatingItems.has(item_0.id);
          const mainImage = item_0.product.images.find(img => img.role === 'main') || item_0.product.images[0];
          return <Card key={item_0.id} className={isUpdating ? 'opacity-60' : ''}>
                <CardContent className="p-3 sm:p-3">
                  <div className="flex gap-3">
                    {/* Product Image */}
                    <Link href={`/${item_0.product.slug}`} className="h-16 w-16 sm:h-18 sm:w-18 flex-shrink-0 rounded bg-muted overflow-hidden">
                      {mainImage ? <img src={mainImage.url} alt={mainImage.altText || item_0.product.name} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center">
                          <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                        </div>}
                    </Link>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <Link href={`/${item_0.product.slug}`} className="font-medium text-sm text-foreground hover:text-primary line-clamp-2">
                        {item_0.product.name}
                      </Link>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        SKU: {item_0.product.sku}
                      </p>

                      <BackorderNotice state={availability.get(item_0.product.sku)?.state ?? 'available'} className="mt-1.5" />

                      {/* Selected Options — compact inline */}
                      {item_0.selectedOptions.length > 0 && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {item_0.selectedOptions.map(opt => `${opt.title}: ${opt.valueTitle}`).join(', ')}
                        </p>}

                      <p className="text-xs text-muted-foreground mt-1">
                        {formatPrice(item_0.calculatedPrice)} each
                      </p>
                    </div>

                    {/* Price + Quantity + Remove */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <p className="text-sm sm:text-base font-semibold">
                        {formatPrice(item_0.lineTotal)}
                      </p>

                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item_0.id, item_0.quantity - 1)} disabled={isUpdating || item_0.quantity <= 1}>
                          <Minus className="h-3 w-3" />
                        </Button>

                        <span className="w-6 text-center text-sm font-medium">
                          {item_0.quantity}
                        </span>

                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item_0.id, item_0.quantity + 1)} disabled={isUpdating}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>

                      <button className="text-xs text-red-600 hover:text-red-700 flex items-center gap-0.5 mt-0.5" onClick={() => removeItem(item_0.id)} disabled={isUpdating}>
                        <Trash2 className="h-3 w-3" />
                        Remove
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky-sidebar">
            <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
              <CardTitle className="text-base sm:text-lg">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-3 sm:space-y-4">
              <div className="flex justify-between text-sm sm:text-base">
                <span className="text-muted-foreground">Subtotal ({cart.itemCount} items)</span>
                <span className="font-medium">{formatPrice(subtotal)}</span>
              </div>

              <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
                <span>Shipping</span>
                <span>Calculated at checkout</span>
              </div>

              <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
                <span>Tax</span>
                <span>Calculated at checkout</span>
              </div>

              <Separator />

              <div className="flex justify-between text-base sm:text-lg font-bold">
                <span>Estimated Total</span>
                <span>{formatPrice(subtotal)}</span>
              </div>

              <Button className="w-full h-12 text-base font-semibold" size="lg" onClick={() => router.push('/checkout')}>
                Proceed to Checkout
              </Button>

              <Button variant="outline" className="w-full h-11 text-sm" onClick={() => router.push('/')}>
                Continue Shopping
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}